import { OperationContext } from '@urql/core';
import {
  CreatePeopleDocument,
  CreatePeopleMutation,
  CreatePeopleMutationVariables,
  PersonInput,
  PersonRole,
  UpdatePeopleDocument,
  UpdatePeopleMutation,
  UpdatePeopleMutationVariables,
} from '../../../types/planung-graphql-client-defs';
import { isNotEmpty } from '../../../utils/typeguards';
import { urqlClient } from '../../../utils/urqlClient';
import { PersonFormValuesType } from '../graphql/types';

type SubmitType = {
  values: PersonFormValuesType;
  uuid?: string;
  pimProfileUuid: string | null;
  personsRole: PersonRole;
  context: Partial<OperationContext>;
};

/** Date-Scalars gehen als String zum Server; die Formularfelder halten teils echte Dates. */
const toScalarDate = (value: Date | string): string => (value instanceof Date ? value.toISOString() : value);
const toScalarDateOrNull = (value: Date | string | null | undefined): string | null =>
  value === null || value === undefined || value === '' ? null : toScalarDate(value);

const toNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

/**
 * Das Backend erwartet immer den vollständigen Stand einer Person: Stammraum, Verträge und
 * Lehrbefähigungen werden ersetzt.
 */
const formToInput = (values: PersonFormValuesType, pimProfileUuid: string | null, role: PersonRole): PersonInput => ({
  pimProfileUuid,
  personalId: values.personalId,
  firstName: values.firstName,
  lastName: values.lastName,
  shortName: values.shortName,
  prefixName: values.prefixName,
  salutation: values.salutation,
  title: values.title,
  email: values.email,
  birthday: toScalarDateOrNull(values.birthday),
  gender: values.gender,
  active: values.active ?? true,
  role,
  defaultRoomUuid: values.defaultRoom || null,
  color: values.timetableConfig?.color || null,
  colorLabel: values.timetableConfig?.colorLabel || null,
  contracts: values.contracts.map((contract) => ({
    name: contract.name,
    billingMethod: contract.billingMethod,
    freelancer: contract.freelancer,
    hoursWeekly: toNumberOrNull(contract.hoursWeekly),
    limitedToFullTime: contract.limitedToFullTime,
    substituteHours: toNumberOrNull(contract.substituteHours),
    validFrom: toScalarDate(contract.validFrom),
    validUntil: toScalarDateOrNull(contract.validUntil),
  })),
  qualifications: values.qualifications
    .filter((qualification) => isNotEmpty(qualification.subject.uuid))
    .map((qualification) => ({
      subjectUuid: qualification.subject.uuid,
      maxGrade: qualification.maxGrade ?? null,
      minGrade: qualification.minGrade ?? null,
      maxTeachingLoad: qualification.maxTeachingLoad ?? null,
    })),
});

export const formSubmit = async ({ values, uuid, pimProfileUuid, personsRole, context }: SubmitType) => {
  const input = formToInput(values, pimProfileUuid, personsRole);

  if (!uuid) {
    return urqlClient
      .mutation<CreatePeopleMutation, CreatePeopleMutationVariables>(CreatePeopleDocument, { input }, context)
      .toPromise();
  }
  return urqlClient
    .mutation<UpdatePeopleMutation, UpdatePeopleMutationVariables>(UpdatePeopleDocument, { uuid, input }, context)
    .toPromise();
};
